import { zodResolver } from "@hookform/resolvers/zod";
import { setPasswordSchema, type SetPasswordInput } from "@clinic/shared";
import { useState, type JSX } from "react";
import { useForm } from "react-hook-form";
import { revealFirstError } from "@web/shared/lib/form-errors";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Button, FormField, Icon, PasswordInput, PersonName } from "@clinic/ui";
import { Logo } from "@web/shared/components/brand/logo";
import { LanguageSwitch } from "@web/shared/components/language-switch";
import { authApi } from "@web/shared/api/auth";
import { BRANDING_SCOPE, useClinicBranding } from "@web/shared/queries/clinic";
import { errorMessageKey } from "@web/shared/lib/api-error";
import { useClinicLogo } from "@web/shared/hooks/use-clinic-logo";
import { ellipsis } from "@web/i18n/ellipsis";

export function SetPasswordPage({ purpose }: { purpose: "activate" | "reset" }): JSX.Element {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const branding = useClinicBranding();
  const logoUrl = useClinicLogo(BRANDING_SCOPE, branding.data?.logoUrl);
  const [formErrorKey, setFormErrorKey] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<SetPasswordInput>({
    mode: "onTouched",
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { token },
  });

  const onSubmit = handleSubmit(
    async (values) => {
      setFormErrorKey(null);

      try {
        await authApi.setPassword(values);
        setDone(true);
      } catch (error) {
        setFormErrorKey(errorMessageKey(error));
      }
    },
    () => revealFirstError(),
  );

  return (
    <main
      data-testid="set-password-page"
      className="flex min-h-full items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-md border border-line rounded-card bg-surface p-8 shadow-card">
        <LanguageSwitch className="mb-4 flex justify-end" />

        <Logo
          size="login"
          src={logoUrl}
          name={branding.data?.name}
          data-testid="set-password-logo"
          className="mb-6"
        />

        {branding.data?.name && (
          <p className="mb-1 text-value font-medium text-ink-muted">
            <PersonName name={branding.data.name} fallback="" />
          </p>
        )}

        <h1 data-testid="set-password-title" className="text-title font-medium text-primary-900">
          {t(purpose === "activate" ? "auth.activateTitle" : "auth.resetTitle")}
        </h1>

        {done ? (
          <>
            <p data-testid="set-password-done" className="mt-1 text-value text-ink-muted">
              {t("auth.passwordSet")}
            </p>
            <Button
              className="mt-6 w-full"
              data-testid="set-password-to-login"
              icon={<Icon name="login" />}
              onClick={() => navigate("/login")}
            >
              {t("auth.toLogin")}
            </Button>
          </>
        ) : (
          <>
            <p className="mt-1 text-value text-ink-muted">{t("auth.setPasswordSubtitle")}</p>

            <form
              data-testid="set-password-form"
              className="mt-4 flex flex-col gap-4"
              onSubmit={onSubmit}
              noValidate
            >
              <FormField
                label="auth.newPassword"
                htmlFor="new-password"
                error={errors.password}
                errorKey={errors.password ? "errors.validation.passwordMin" : undefined}
              >
                <PasswordInput
                  placeholder={t("common.placeholders.password")}
                  id="new-password"
                  data-testid="set-password-input"
                  autoComplete="new-password"
                  hasError={errors.password !== undefined}
                  {...register("password")}
                />
              </FormField>

              {formErrorKey !== null && (
                <p
                  role="alert"
                  data-testid="set-password-error"
                  className="rounded-panel bg-danger-50 px-3.5 py-2.5 text-value text-danger-700"
                >
                  {t(formErrorKey)}
                </p>
              )}

              <Button
                icon={<Icon name="check" />}
                type="submit"
                aria-disabled={!isValid || isSubmitting || undefined}
                data-testid="set-password-submit"
                isLoading={isSubmitting}
                className="mt-2 w-full"
              >
                {isSubmitting ? ellipsis(t("auth.submitting")) : t("auth.setPassword")}
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
